import {naturalSortByKey} from '@benchling/js-utils';
import reactUpdate from 'immutability-helper';
import _ from 'underscore';

import {ArchiveRecord} from 'archive/ArchiveConstants';
import isArchived from 'archive/isArchived';
import {EventConstants} from 'constants/EventConstants';
import AppDispatcher, {FluxAction} from 'lib/Dispatcher';
import Store from 'lib/Store';
import {SchemaFieldSelector, SchemaFieldSelectorOptionToUpdate} from 'schemas/SchemaFieldSelectorConstants';

export class SchemaFieldSelectorStore extends Store {
  selectorsByRegistryId: {[registryId: string]: Array<SchemaFieldSelector>} = {};
  selectorOptionToUpdate: SchemaFieldSelectorOptionToUpdate | null = null;

  override resetState(): void {
    // Includes archived selectors; `getOrderedSelectorsForRegistry` filters them out
    this.selectorsByRegistryId = {};
    this.selectorOptionToUpdate = null;
  }

  getAllSelectorsById({includeArchived}: {includeArchived: boolean}): _.Dictionary<SchemaFieldSelector> {
    let selectors: Array<SchemaFieldSelector> = [];
    for (const registryId of Object.keys(this.selectorsByRegistryId)) {
      const selectorsForRegistry = this.getOrderedSelectorsForRegistry(registryId, {includeArchived});
      if (selectorsForRegistry) {
        selectors = [...selectors, ...selectorsForRegistry];
      }
    }
    return _.indexBy(selectors, 'id');
  }

  getOrderedSelectorsForRegistry(
    registryId: string,
    {includeArchived}: {includeArchived?: boolean} = {}
  ): Array<SchemaFieldSelector> | null {
    if (!(registryId in this.selectorsByRegistryId)) {
      return null;
    }
    const result = includeArchived
      ? this.selectorsByRegistryId[registryId]
      : this.selectorsByRegistryId[registryId].filter(
          (selector: SchemaFieldSelector) => !isArchived(selector)
        );
    return naturalSortByKey(result, `name`);
  }

  getOrderedSelectors(includeArchived: boolean = false): Array<SchemaFieldSelector> | null {
    if (_.isEmpty(this.selectorsByRegistryId)) {
      return null;
    }
    const selectors: Array<SchemaFieldSelector> = [];
    for (const registryId of Object.keys(this.selectorsByRegistryId)) {
      const selectorsForRegistry = this.getOrderedSelectorsForRegistry(registryId, {includeArchived});
      if (selectorsForRegistry) {
        selectors.push(...selectorsForRegistry);
      }
    }
    return naturalSortByKey(selectors, `name`);
  }

  getSelectorById(selectorId: string): SchemaFieldSelector | null {
    for (const registryId of Object.keys(this.selectorsByRegistryId)) {
      const selector = _.findWhere(this.selectorsByRegistryId[registryId], {id: selectorId});
      if (selector) {
        return selector;
      }
    }
    return null;
  }

  getRegistryIdForSelector(selectorId: string): string | null {
    for (const registryId of Object.keys(this.selectorsByRegistryId)) {
      const selector = _.findWhere(this.selectorsByRegistryId[registryId], {id: selectorId});
      if (selector) {
        return registryId;
      }
    }
    return null;
  }

  getSelectorOptionToUpdate(): SchemaFieldSelectorOptionToUpdate | null {
    return this.selectorOptionToUpdate;
  }

  _addSelector(registryId: string, selector: SchemaFieldSelector): void {
    this.selectorsByRegistryId = reactUpdate(this.selectorsByRegistryId, {[registryId]: {$push: [selector]}});
  }

  _removeSelector(selectorId: string): void {
    // Maintain ref equality
    this.selectorsByRegistryId = {...this.selectorsByRegistryId};
    Object.keys(this.selectorsByRegistryId).forEach((registryId) => {
      // eslint-disable-next-line no-restricted-syntax
      this.selectorsByRegistryId[registryId] = _.reject(
        this.selectorsByRegistryId[registryId],
        (selector: SchemaFieldSelector): boolean => selector.id === selectorId
      );
    });
  }

  _archiveSelector(selectorId: string, archiveRecord: ArchiveRecord): void {
    const oldSelector = this.getSelectorById(selectorId);
    const registryId = this.getRegistryIdForSelector(selectorId);
    if (oldSelector && registryId) {
      this._updateSelector(registryId, {...oldSelector, archiveRecord});
    }
  }

  _unarchiveSelector(selectorId: string): void {
    const oldSelector = this.getSelectorById(selectorId);
    // When a selector is unarchived, so are its options, and those option IDs are included in the response.
    // This check is necessary to ensure that we don't attempt to look up the selector option ID.
    const registryId = this.getRegistryIdForSelector(selectorId);
    if (oldSelector && registryId) {
      this._updateSelector(registryId, {...oldSelector, archiveRecord: null});
    }
  }

  _updateSelector(registryId: string, selector: SchemaFieldSelector): void {
    // eslint-disable-next-line no-restricted-syntax
    const index = _.findIndex(this.selectorsByRegistryId[registryId], (s) => s.id === selector.id);
    this.selectorsByRegistryId = reactUpdate(this.selectorsByRegistryId, {
      [registryId]: {[index]: {$set: selector}},
    });
  }

  override payloadHandler(action: FluxAction): boolean {
    switch (action.actionType) {
      case EventConstants.SCHEMA_FIELD_SELECTORS_FETCH_COMPLETED: {
        // Cache selectors so that switching registries does not require refetching
        this.selectorsByRegistryId = {...this.selectorsByRegistryId, ...action.selectorsByRegistryId};
        this.emitChange();
        break;
      }

      case EventConstants.SCHEMA_FIELD_SELECTOR_DELETE_COMPLETED: {
        this._removeSelector(action.selectorId);
        this.emitChange();
        break;
      }

      case EventConstants.BULK_ARCHIVE_SCHEMA_FIELD_SELECTORS_COMPLETED: {
        const {itemIds, archiveRecordsByItemId} = action;
        itemIds.forEach((itemId: string) => {
          this._archiveSelector(itemId, archiveRecordsByItemId[itemId]);
        });
        this.emitChange();
        break;
      }

      case EventConstants.BULK_UNARCHIVE_SCHEMA_FIELD_SELECTORS_COMPLETED: {
        const {itemIds} = action;
        itemIds.forEach((itemId: string) => {
          this._unarchiveSelector(itemId);
        });
        this.emitChange();
        break;
      }

      case EventConstants.SCHEMA_FIELD_SELECTOR_CREATE_COMPLETED: {
        this._addSelector(action.registryId, action.selector);
        this.emitChange();
        break;
      }

      case EventConstants.SCHEMA_FIELD_SELECTOR_UPDATE_CANCELED: {
        this.selectorOptionToUpdate = null;
        this.emitChange();
        break;
      }

      case EventConstants.SCHEMA_FIELD_SELECTOR_UPDATE_COMPLETED: {
        this._updateSelector(action.registryId, action.selector);
        this.selectorOptionToUpdate = null;
        this.emitChange();
        break;
      }

      case EventConstants.SCHEMA_FIELD_SELECTOR_UPDATE_FAILED: {
        this.selectorOptionToUpdate = action.selectorOptionToUpdate;
        this.emitChange();
        break;
      }

      default:
        break;
    }

    return true;
  }
}

export default new SchemaFieldSelectorStore(AppDispatcher);
